import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.scss";

export interface TooltipProps {
  /** Texte complet affiché dans l'infobulle au survol */
  content?: ReactNode;
  /** Élément déclencheur (le contenu visible, souvent tronqué) */
  children: ReactNode;
}

/**
 * Infobulle rendue dans un portail (document.body) pour ne jamais être
 * coupée par un conteneur scrollable / overflow. La position est calculée
 * à partir du déclencheur au survol. N'affiche rien si `content` est vide.
 */
export default function Tooltip({ content, children }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  if (!content) {
    return <>{children}</>;
  }

  const show = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, left: rect.left });
  };

  const hide = () => setCoords(null);

  return (
    <span
      ref={triggerRef}
      className={styles["tooltip"]}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
    >
      {children}
      {coords &&
        createPortal(
          <span
            className={styles["tooltip__bubble"]}
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
}
